import { IModel, IViolation, IAttribute, ModelConstants } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { HDInsightLinkedService } from 'app/model/linkedservice/hdinsight.linkedservice';
import { FilePath } from 'app/model/dataset/file-path';

import { format } from 'app/shared/utils/string.util';
import { createParameterConfigurationValidator } from 'app/model/adapters/linkedservices/parameter-config.util';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { HDInsightOnDemandLinkedService } from 'app/model/linkedservice/hdinsightondemand.linkedservice';
import { validateFilePath } from 'app/shared/utils/activity-validation.util';

@Metamodel.model({
    name: ModelConstants.streamingActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.StreamingActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.StreamingActivityLabel)
    ],
    referable: true
})
export class StreamingActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly MAPPERFILEPATH: IAttribute;
    public static readonly REDUCERFILEPATH: IAttribute;
    public static readonly MAPPER: IAttribute;
    public static readonly REDUCER: IAttribute;
    public static readonly INPUT: IAttribute;
    public static readonly OUTPUT: IAttribute;
    public static readonly ARGUMENTS: IAttribute;

    @Metamodel.attribute({
        display: stringResources.ModelName_Arguments,
        association: 'ARGUMENTS',
        templatable: true,
        accepts: ['Array']
    })
    public streamingActivityArguments?: ParameterConfig[]; // Property name is "arguments" in JSON, needs to workaround with tslint for banned term.
    public getDebugInfo?: string;
    @Metamodel.attribute({
        display: stringResources.ModelName_Defines,
        constraint: {
            validator: createParameterConfigurationValidator()
        }
    })
    public defines?: ParameterConfig[];
    @Metamodel.attribute({
        display: stringResources.FilePathForMapperLabel,
        constraint: {
            required: true,
            validator: StreamingActivity.validateMapperFilePath
        },
        association: 'MAPPERFILEPATH'
    })
    public mapperFilePath = new FilePath();
    @Metamodel.attribute({
        display: stringResources.FilePathForReducerLabel,
        constraint: {
            required: true,
            validator: StreamingActivity.validateReducerFilePath
        },
        association: 'REDUCERFILEPATH'
    })
    public reducerFilePath = new FilePath();
    @Metamodel.attribute({
        display: stringResources.ModelName_Mapper,
        constraint: {
            required: true
        },
        association: 'MAPPER',
        templatable: true
    })
    public mapper?: string;
    @Metamodel.attribute({
        display: stringResources.ReducerLabel,
        constraint: {
            required: true
        },
        association: 'REDUCER',
        templatable: true
    })
    public reducer?: string;
    @Metamodel.attribute({
        display: stringResources.InputLabel,
        constraint: {
            required: true
        },
        association: 'INPUT',
        templatable: true
    })
    public input?: string;
    @Metamodel.attribute({
        display: stringResources.OutputLabel,
        constraint: {
            required: true
        },
        association: 'OUTPUT',
        templatable: true
    })
    public output?: string;
    @Metamodel.attribute({
        display: stringResources.FileLinkedServiceLabel,
        reference: true,
        type: AzureStorageLinkedService.MODEL
    })
    public fileLinkedService?: AzureStorageLinkedService; // Azure Storage linked service which contains the jar.
    @Metamodel.attribute({
        display: format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Hdi_DisplayName),
        constraint: {
            required: true
        },
        reference: true,
        type: HDInsightLinkedService.MODEL
    })
    public linkedService?: HDInsightLinkedService | HDInsightOnDemandLinkedService;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static validateMapperFilePath(_activity?: StreamingActivity, arg?: FilePath | undefined): IViolation[] | undefined {
        return validateFilePath(_activity, arg, StreamingActivity.MAPPERFILEPATH);
    }

    private static validateReducerFilePath(_activity?: StreamingActivity, arg?: FilePath | undefined): IViolation[] | undefined {
        return validateFilePath(_activity, arg, StreamingActivity.REDUCERFILEPATH);
    }
}
